import '../css/app.css'
import './bootstrap'
import { createAutoAnimatePlugin } from '@formkit/addons'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import {
    autocomplete,
    createProPlugin,
    dropdown,
    repeater,
    taglist,
    toggle,
} from '@formkit/pro'
import { generateClasses } from '@formkit/themes'
import {
    defaultConfig as formkitDefaultConfig,
    plugin as formkitPlugin,
} from '@formkit/vue'
import { createInertiaApp } from '@inertiajs/vue3'
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp, Fragment, h } from 'vue'
import { plugin as VueTippy } from 'vue-tippy'
import { ZiggyVue } from 'ziggy-js'
import formkitTheme from './formkit.theme'
// import { plugin as inertiaPlugin } from 'formkit-addon-inertia'
import 'tippy.js/dist/tippy.css' // optional for styling

// import 'tippy.js/animations/shift-toward.css'
// import 'tippy.js/dist/backdrop.css'
// import 'tippy.js/animations/shift-away.css'
import { router } from '@inertiajs/vue3'
import { AnalyticsBrowser } from '@segment/analytics-next'
//import * as Sentry from '@sentry/vue'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { i18nVue } from 'laravel-vue-i18n'
import { MixpanelPlugin } from './lib/mixpanel'
import { PostHogPlugin } from './lib/posthog'
//import { setSentryUser } from './lib/sentryUtils'
import type { Admin, User } from './store/userStore'
import { brand } from './utils/themeColors'

const pro = createProPlugin('fk-6e8f4ef850', {
    autocomplete,
    dropdown,
    taglist,
    toggle,
    repeater,
})

const pinia = createPinia()
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5, // default cache duration for queries (5 minutes)
            retry: (failureCount, error) => {
                // Don't retry on auth errors - the axios interceptor will handle redirect
                if (
                    error instanceof Error &&
                    (error.message.includes('401') ||
                        error.message.includes('Unauthenticated'))
                ) {
                    return false
                }
                return failureCount < 1
            },
        },
    },
})

;(async function () {
    createInertiaApp({
        progress: {
            color: brand[500],
            delay: 0,
        },
        // @ts-ignore
        resolve: name => {
            if (name.startsWith('v2/')) {
                const page = name.substring(3)
                return resolvePageComponent(
                    `./v2/${page}.vue`,
                    import.meta.glob('./v2/**/*.vue')
                )
            }
            return resolvePageComponent(
                `./pages/${name}.vue`,
                import.meta.glob('./pages/**/*.vue')
            )
        },
        setup({ el, App, props, plugin }) {
            const app = createApp({
                render: () => h(Fragment, [h(App, props)]),
            })
                .use(plugin)
                // @ts-ignore
                .use(pinia)
                .use(VueQueryPlugin, {
                    queryClient,
                    enableDevtoolsV6Plugin: true,
                })
                .use(ZiggyVue)
                .use(autoAnimatePlugin)
                .use(
                    formkitPlugin,
                    formkitDefaultConfig({
                        plugins: [
                            pro,
                            // inertiaPlugin,
                            createAutoAnimatePlugin(),
                        ],
                        config: {
                            classes: generateClasses(formkitTheme),
                        },
                    })
                )
                .use(VueTippy, {
                    // animation: 'shift-toward',
                    defaultProps: {
                        animation: 'fade',
                        // delay: 250,
                        duration: 500,
                        placement: 'bottom',
                        arrow: false,
                        // inertia: true,
                        // animateFill: true,
                    },
                })
                .use(i18nVue, {
                    resolve: lang => import(`../../lang/${lang}.json`),
                })
                .mixin({
                    methods: {
                        // @ts-ignore
                        route: window.route,
                        // @ts-ignore
                        moment: window.moment || (() => {}),
                    },
                })

            const segmentWriteKey = import.meta.env.VITE_SEGMENT_WRITE_KEY

            const analytics = AnalyticsBrowser.load(
                {
                    writeKey: segmentWriteKey,
                    cdnURL: 'https://touch.snitcher.com',
                },
                {
                    integrations: {
                        'Segment.io': { apiHost: 'touch-api.snitcher.com/v1' },
                    },
                }
            )

            app.config.globalProperties.$analytics = analytics

            // Initialize PostHog
            app.use(PostHogPlugin, {
                apiKey:
                    import.meta.env.VITE_POSTHOG_API_KEY ||
                    'phc_FzYe9YehSSJE6tUJfaJSXIfSqkmBT6wNoeptVTrrOB8',
                apiHost:
                    import.meta.env.VITE_POSTHOG_API_HOST ||
                    'https://eu.i.posthog.com',
                enableSessionRecording: true,
                enableHeatmaps: true,
                capturePageview: false, // We handle this manually
            })

            // Initialize Mixpanel
            app.use(MixpanelPlugin, {
                token:
                    import.meta.env.VITE_MIXPANEL_TOKEN ||
                    'bc4f95ffda50b558021a92e491d033c8',
                apiHost:
                    import.meta.env.VITE_MIXPANEL_API_HOST ||
                    'https://api-eu.mixpanel.com',
                debug: import.meta.env.DEV,
                trackPageviews: false, // We handle this manually
            })

            // Sentry disabled temporarily
            // Sentry.init({
            //     app,
            //     dsn: import.meta.env.VITE_SENTRY_DSN || '',
            //     beforeSend: (event: Sentry.ErrorEvent) => {
            //         event.tags = { ...(event.tags || {}), source: 'frontend' }
            //         return event
            //     },
            //     environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local',
            //     sendDefaultPii: true,
            //     tracesSampleRate: parseFloat(
            //         import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
            //     ),
            //     tracePropagationTargets: [
            //         /^https:\/\/snitcher\.io/,
            //         /^https:\/\/.*\.snitcher\.io/,
            //         /^https:\/\/touch\.snitcher\.com/,
            //         'localhost',
            //         'snitcher.dev',
            //     ],
            //     integrations: [
            //         Sentry.browserTracingIntegration(),
            //         Sentry.replayIntegration({
            //             maskAllText: false,
            //             blockAllMedia: false,
            //         }),
            //     ],
            //     replaysSessionSampleRate: parseFloat(
            //         import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ||
            //             '0.1'
            //     ),
            //     replaysOnErrorSampleRate: parseFloat(
            //         import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ||
            //             '1.0'
            //     ),
            // })

            router.on('navigate', event => {
                const pageProps = event.detail.page.props
                const isImpersonating = (pageProps.admin as Admin)
                    ?.is_impersonating

                // Skip analytics tracking when staff is impersonating
                if (!isImpersonating) {
                    analytics.page()
                }

                const userInfo = {
                    email: (pageProps.user as User)?.email ?? pageProps.currentUser?.email,
                    id: (pageProps.user as User)?.id ?? pageProps.currentUser?.hashid,
                    isAdminImpersonating: isImpersonating,
                }

                // if (userInfo.id) setSentryUser(userInfo)

                // Sentry.addBreadcrumb({
                //     category: 'navigation',
                //     message: `Navigate to ${event.detail.page.component}`,
                //     level: 'info',
                //     data: {
                //         from: document.referrer,
                //         to: window.location.href,
                //         component: event.detail.page.component,
                //     },
                // })
            })

            // Handle invalid responses (session expired, server errors)
            router.on('invalid', event => {
                const status = event.detail.response?.status

                // Session expired - 409 with X-Inertia-Location triggers hard reload
                // But if we get 401 directly, handle it
                if (status === 401) {
                    event.preventDefault()
                    window.location.href = '/login?session_expired=1'
                }
            })

            // Handle exceptions from Inertia visits
            router.on('exception', event => {
                console.error('Inertia exception:', event.detail.exception)
            })

            app.mount(el)
        },
    })
})()
